/**
 * Taskbolt i18n translations
 * These are added to the shell's i18next instance under the 'taskbolt' namespace
 */
export const taskboltTranslations = {
  en: {
    sidebar: {
      selectProject: "Select project",
      projects: "Projects",
      home: "Home",
      personalWorkspace: "Personal workspace",
      activeSprint: "Active Sprint",
      backlogs: "Backlogs",
      reports: "Reports",
      configure: "Configure",
      collapse: "Collapse sidebar",
      expand: "Expand sidebar",
    },
    header: {
      searchPlaceholder: "Search issues...",
      newIssue: "New issue",
    },
    activeSprint: {
      title: "Active Sprint",
      noSprint: "No active sprint",
      selectProjectPrompt: "Select a project from the sidebar to see its active sprint.",
      empty: "No issues in this sprint",
      addTask: "Add task",
      completeSprint: "Complete sprint",
      completingSprint: "Completing…",
      completeSprintConfirm: "Complete this sprint? It will move out of Active Sprint.",
    },
    backlogs: {
      title: "Backlogs",
      empty: "No backlog items",
      backlogBucket: "Backlog",
      filters: "Filters",
      filterStatus: "Status",
      filterPriority: "Priority",
      clearFilters: "Clear filters",
    },
    reports: {
      title: "Reports",
      subtitle: "Track sprint velocity, completion, and task breakdown.",
      filterSprint: "Sprint",
      filterSprintPlaceholder: "Select a sprint",
      noSprints: "This project has no sprints yet.",
      kpiProgress: "Progress",
      kpiTasksDone: "Tasks done",
      kpiTasksDoneSub: "{{count}} remaining",
      kpiStoryPoints: "Story points",
      kpiStoryPointsSub: "{{count}} pts left",
      kpiTimeLeft: "Time left",
      kpiTimeLeftSuffix: "days",
      kpiTimeLeftSub: "Ends {{date}}",
      burndownTitle: "Burndown",
      burndownActual: "Actual",
      burndownIdeal: "Ideal",
      burndownEmpty: "No story points in this sprint yet.",
      burndownStart: "Start",
      burndownEnd: "End",
      burndownToday: "Today",
      breakdownTitle: "Task breakdown",
      breakdownEmpty: "No tasks in this sprint yet.",
      tasksTitle: "Tasks",
      tasksEmpty: "No tasks in this sprint yet.",
      viewBoard: "View board",
      tasksKey: "Key",
      tasksTask: "Task",
      tasksStatus: "Status",
      tasksAssignee: "Assignee",
      tasksPoints: "Points",
    },
    configure: {
      title: "Configure",
      projectManagement: "Project Management",
      projectManagementDescription:
        "Manage project setup, members, and delivery settings.",
      noProjects: "No projects found",
      selectProjectPrompt: "Select a project from the sidebar to configure it.",
      comingSoon: "Coming soon",
      plannedBadge: "Planned",
      boardsTitle: "Boards & columns",
      boardsDescription:
        "Kanban columns, WIP limits, swimlanes and card fields shown on the board.",
      workflowTitle: "Workflow",
      workflowDescription:
        "Statuses, transitions and automation rules that move tasks across the board.",
      notificationsTitle: "Notifications",
      notificationsDescription:
        "Choose which events notify the team — mentions, moves, blockers, due dates.",
    },
    project: {
      title: "Project Details",
      editTitle: "Edit Project",
      createTitle: "Create Project",
      addProject: "Add Project",
      nameLabel: "Name",
      namePlaceholder: "Enter project name",
      clientNameLabel: "Client Name",
      clientNamePlaceholder: "Enter client name",
      descriptionLabel: "Description",
      descriptionPlaceholder: "Enter project description...",
      startDateLabel: "Start Date",
      endDateLabel: "End Date",
      statusLabel: "Status",
      typeLabel: "Type",
      backToConfigure: "Back to Configure",
    },
    member: {
      title: "Members & permissions",
      subtitle:
        "Invite people, group them into teams and control who can edit sprints, tasks and reports.",
      addMember: "Add Member",
      noMembers: "No members yet",
      addMemberTitle: "Add Member",
      editMemberTitle: "Edit Member",
      userLabel: "User",
      userPlaceholder: "Select a user",
      noEligibleUsers: "No eligible users to add",
      roleLabel: "Role",
      confirmRemovePrefix: "Remove",
      confirmRemoveSuffix: "from this project?",
    },
    memberRole: {
      ADMIN: "Admin",
      MEMBER: "Member",
      VIEWER: "Viewer",
    },
    projectStatus: {
      going: "Going",
      plan: "Plan",
      cancelled: "Cancelled",
      closed: "Closed",
    },
    projectType: {
      outsource: "Outsource",
      product: "Product",
      poc: "POC",
    },
    sprint: {
      title: "Sprints",
      createTitle: "Create Sprint",
      editTitle: "Edit Sprint",
      nameLabel: "Name",
      namePlaceholder: "e.g., Sprint 1",
      goalLabel: "Goal",
      goalPlaceholder: "What do you want to achieve in this sprint?",
      startDateLabel: "Start Date",
      endDateLabel: "End Date",
      statusLabel: "Status",
      noSprints: "No sprints yet",
      addSprint: "Add Sprint",
      activeSprintLocked: "An active sprint can't be edited or deleted",
      markAsDone: "Mark sprint as done",
      markAsDoneConfirm: "Mark this sprint as done? It will be completed.",
    },
    sprintStatus: {
      planned: "Planning",
      active: "Active",
      completed: "Completed",
    },
    taskDetailHeader: {
      taskId: "Task ID",
      copied: "Copied",
      copyLink: "Copy link to task page",
      createSubTask: "Create sub-task",
      convertToSubTask: "Convert to sub-task",
      convertToTask: "Convert to task",
      deleteTask: "Delete task",
    },
    taskDetail: {
      detailsLabel: "Details",
      subtasksLabel: "Subtasks",
      subtasksProgress: "{{done}} of {{total}} done",
      noSubtasks: "No subtasks yet",
      assigneeLabel: "Assignee",
      reporterLabel: "Reporter",
      sprintLabel: "Sprint",
      unassigned: "Unassigned",
      noSprint: "No sprint",
      updatedLabel: "Updated",
      lastEditedPrefix: "Last edited",
      commentsLabel: "Comments",
      commentPlaceholder: "Write a comment…",
      noComments: "No comments yet",
      unknownUser: "Unknown user",
      you: "You",
      commentSubmit: "Comment",
      commentUpdate: "Update comment",
      commentCancel: "Cancel",
      commentEdit: "Edit comment",
      commentDelete: "Delete comment",
      commentDeleteConfirm: "Delete this comment? This cannot be undone.",
      taskDeleteConfirm: "Delete this task? This cannot be undone.",
      backToBacklogs: "Back to Backlogs",
    },
    common: {
      save: "Save",
      cancel: "Cancel",
      edit: "Edit",
      delete: "Delete",
      create: "Create",
      loading: "Loading...",
      noData: "No data available",
    },
    taskLink: {
      notFound: "This task couldn't be found, or you don't have access to it.",
    },
    taskForm: {
      createTitle: "Create Task",
      editTitle: "Edit Task",
      titleLabel: "Title",
      titlePlaceholder: "Enter task title",
      descriptionLabel: "Description",
      descriptionPlaceholder: "Enter task description...",
      typeLabel: "Type",
      statusLabel: "Status",
      priorityLabel: "Priority",
      storyPointLabel: "Story Points",
      storyPointPlaceholder: "e.g., 1, 2, 3, 5, 8",
      createdLabel: "Created",
      assigneeLabel: "Assignee",
      sprintLabel: "Sprint",
      noSprintOption: "No sprint",
      submitButton: "Create Task",
      updateButton: "Update Task",
      cancelButton: "Cancel",
      requiredField: "This field is required",
    },
    taskType: {
      STORY: "Story",
      EPIC: "Epic",
      ISSUE: "Issue",
      BUG: "Bug",
    },
    taskStatus: {
      TODO: "To Do",
      IN_PROGRESS: "In Progress",
      IN_REVIEW: "In Review",
      DONE: "Done",
      CLOSED: "Closed",
    },
    taskPriority: {
      HIGHEST: "Highest",
      HIGH: "High",
      MEDIUM: "Medium",
      LOW: "Low",
    },
    toast: {
      taskCreated: "Task created successfully",
      taskUpdated: "Task updated successfully",
      taskDeleted: "Task deleted successfully",
      taskCreateFailed: "Failed to create task",
      taskUpdateFailed: "Failed to update task",
      taskDeleteFailed: "Failed to delete task",
      commentAdded: "Comment added",
      commentAddFailed: "Failed to add comment",
      commentsLoadFailed: "Failed to load comments",
      commentUpdateFailed: "Failed to update comment",
      commentDeleteFailed: "Failed to delete comment",
      projectCreated: "Project created successfully",
      projectCreateFailed: "Failed to create project",
      projectUpdated: "Project updated successfully",
      projectUpdateFailed: "Failed to update project",
      memberAdded: "Member added successfully",
      memberUpdated: "Member updated successfully",
      memberRemoved: "Member removed successfully",
      memberAddFailed: "Failed to add member",
      memberUpdateFailed: "Failed to update member",
      memberRemoveFailed: "Failed to remove member",
      sprintCreated: "Sprint created successfully",
      sprintUpdated: "Sprint updated successfully",
      sprintDeleted: "Sprint deleted successfully",
      sprintCompleted: "Sprint completed successfully",
      sprintCreateFailed: "Failed to create sprint",
      sprintUpdateFailed: "Failed to update sprint",
      sprintDeleteFailed: "Failed to delete sprint",
      sprintCompleteFailed: "Failed to complete sprint",
      apiClientUnavailable: "API client not available",
      genericError: "Something went wrong",
      membersLoadFailed: "Failed to load project members",
      sprintsLoadFailed: "Failed to load sprints",
    },
    widgets: {
      pomodoro: {
        title: "Pomodoro Timer",
        start: "Start",
        pause: "Pause",
        reset: "Reset",
      },
      greeting: {
        morning: "Good morning",
        afternoon: "Good afternoon",
        evening: "Good evening",
        description: "A friendly greeting to you my friend",
      },
    },
  },
  vi: {
    sidebar: {
      selectProject: "Chọn dự án",
      projects: "Dự án",
      personalWorkspace: "Không gian cá nhân",
      home: "Trang chủ",
      activeSprint: "Sprint hiện tại",
      backlogs: "Backlogs",
      reports: "Báo cáo",
      configure: "Cấu hình",
      collapse: "Thu gọn thanh bên",
      expand: "Mở rộng thanh bên",
    },
    header: {
      searchPlaceholder: "Tìm kiếm issue...",
      newIssue: "Tạo issue mới",
    },
    activeSprint: {
      title: "Sprint hiện tại",
      noSprint: "Không có sprint nào đang hoạt động",
      selectProjectPrompt: "Chọn một dự án ở thanh bên để xem sprint hiện tại của dự án đó.",
      empty: "Không có issue nào trong sprint này",
      addTask: "Thêm công việc",
      completeSprint: "Hoàn thành sprint",
      completingSprint: "Đang hoàn thành…",
      completeSprintConfirm: "Hoàn thành sprint này? Sprint sẽ không còn hiển thị ở Sprint hiện tại.",
    },
    backlogs: {
      title: "Backlogs",
      empty: "Không có mục nào trong backlog",
      backlogBucket: "Backlog",
      filters: "Bộ lọc",
      filterStatus: "Trạng thái",
      filterPriority: "Độ ưu tiên",
      clearFilters: "Xóa bộ lọc",
    },
    reports: {
      title: "Báo cáo",
      subtitle: "Theo dõi tốc độ, tỷ lệ hoàn thành và phân loại công việc theo sprint.",
      filterSprint: "Sprint",
      filterSprintPlaceholder: "Chọn sprint",
      noSprints: "Dự án này chưa có sprint nào.",
      kpiProgress: "Tiến độ",
      kpiTasksDone: "Công việc hoàn thành",
      kpiTasksDoneSub: "Còn {{count}}",
      kpiStoryPoints: "Điểm công việc",
      kpiStoryPointsSub: "Còn {{count}} điểm",
      kpiTimeLeft: "Thời gian còn lại",
      kpiTimeLeftSuffix: "ngày",
      kpiTimeLeftSub: "Kết thúc {{date}}",
      burndownTitle: "Burndown",
      burndownActual: "Thực tế",
      burndownIdeal: "Lý tưởng",
      burndownEmpty: "Sprint này chưa có điểm công việc nào.",
      burndownStart: "Bắt đầu",
      burndownEnd: "Kết thúc",
      burndownToday: "Hôm nay",
      breakdownTitle: "Phân loại công việc",
      breakdownEmpty: "Sprint này chưa có công việc nào.",
      tasksTitle: "Công việc",
      tasksEmpty: "Sprint này chưa có công việc nào.",
      viewBoard: "Xem bảng",
      tasksKey: "Mã",
      tasksTask: "Công việc",
      tasksStatus: "Trạng thái",
      tasksAssignee: "Người phụ trách",
      tasksPoints: "Điểm",
    },
    configure: {
      title: "Cấu hình",
      projectManagement: "Quản lý dự án",
      projectManagementDescription:
        "Quản lý thiết lập dự án, thành viên và cấu hình triển khai.",
      noProjects: "Không có dự án nào",
      selectProjectPrompt: "Chọn một dự án từ thanh bên để cấu hình.",
      comingSoon: "Sắp ra mắt",
      plannedBadge: "Dự kiến",
      boardsTitle: "Bảng & cột",
      boardsDescription:
        "Cột Kanban, giới hạn WIP, làn công việc và các trường hiển thị trên bảng.",
      workflowTitle: "Quy trình",
      workflowDescription:
        "Trạng thái, chuyển tiếp và quy tắc tự động di chuyển công việc trên bảng.",
      notificationsTitle: "Thông báo",
      notificationsDescription:
        "Chọn sự kiện nào sẽ thông báo cho nhóm — nhắc đến, di chuyển, vướng mắc, hạn chót.",
    },
    project: {
      title: "Chi tiết dự án",
      editTitle: "Chỉnh sửa dự án",
      createTitle: "Tạo dự án",
      addProject: "Thêm dự án",
      nameLabel: "Tên",
      namePlaceholder: "Nhập tên dự án",
      clientNameLabel: "Tên khách hàng",
      clientNamePlaceholder: "Nhập tên khách hàng",
      descriptionLabel: "Mô tả",
      descriptionPlaceholder: "Nhập mô tả dự án...",
      startDateLabel: "Ngày bắt đầu",
      endDateLabel: "Ngày kết thúc",
      statusLabel: "Trạng thái",
      typeLabel: "Loại",
      backToConfigure: "Quay lại Cấu hình",
    },
    member: {
      title: "Thành viên & phân quyền",
      subtitle:
        "Mời mọi người, nhóm họ thành các đội và kiểm soát ai có thể chỉnh sửa sprint, công việc và báo cáo.",
      addMember: "Thêm thành viên",
      noMembers: "Chưa có thành viên",
      addMemberTitle: "Thêm thành viên",
      editMemberTitle: "Chỉnh sửa thành viên",
      userLabel: "Người dùng",
      userPlaceholder: "Chọn người dùng",
      noEligibleUsers: "Không có người dùng phù hợp để thêm",
      roleLabel: "Vai trò",
      confirmRemovePrefix: "Xóa",
      confirmRemoveSuffix: "khỏi dự án này?",
    },
    memberRole: {
      ADMIN: "Quản trị viên",
      MEMBER: "Thành viên",
      VIEWER: "Người xem",
    },
    projectStatus: {
      going: "Đang thực hiện",
      plan: "Lập kế hoạch",
      cancelled: "Đã hủy",
      closed: "Đã đóng",
    },
    projectType: {
      outsource: "Thuê ngoài",
      product: "Sản phẩm",
      poc: "POC",
    },
    sprint: {
      title: "Sprint",
      createTitle: "Tạo Sprint",
      editTitle: "Chỉnh sửa Sprint",
      nameLabel: "Tên",
      namePlaceholder: "vd: Sprint 1",
      goalLabel: "Mục tiêu",
      goalPlaceholder: "Bạn muốn đạt được gì trong sprint này?",
      startDateLabel: "Ngày bắt đầu",
      endDateLabel: "Ngày kết thúc",
      statusLabel: "Trạng thái",
      noSprints: "Chưa có sprint nào",
      addSprint: "Thêm Sprint",
      activeSprintLocked: "Không thể sửa hoặc xóa sprint đang hoạt động",
      markAsDone: "Đánh dấu sprint đã hoàn thành",
      markAsDoneConfirm: "Đánh dấu sprint này là đã hoàn thành?",
    },
    sprintStatus: {
      planned: "Lập kế hoạch",
      active: "Đang hoạt động",
      completed: "Hoàn thành",
    },
    taskDetailHeader: {
      taskId: "Mã công việc",
      copied: "Đã sao chép",
      copyLink: "Sao chép liên kết đến trang công việc",
      createSubTask: "Tạo sub-task",
      convertToSubTask: "Chuyển thành sub-task",
      convertToTask: "Chuyển thành công việc",
      deleteTask: "Xóa công việc",
    },
    taskDetail: {
      detailsLabel: "Chi tiết",
      subtasksLabel: "Công việc con",
      subtasksProgress: "{{done}}/{{total}} hoàn thành",
      noSubtasks: "Chưa có công việc con",
      assigneeLabel: "Người thực hiện",
      reporterLabel: "Người tạo",
      sprintLabel: "Sprint",
      unassigned: "Chưa giao",
      noSprint: "Không có sprint",
      updatedLabel: "Cập nhật",
      lastEditedPrefix: "Chỉnh sửa lần cuối",
      commentsLabel: "Bình luận",
      commentPlaceholder: "Viết bình luận…",
      noComments: "Chưa có bình luận nào",
      unknownUser: "Người dùng không xác định",
      you: "Bạn",
      commentSubmit: "Bình luận",
      commentUpdate: "Cập nhật bình luận",
      commentCancel: "Hủy",
      commentEdit: "Sửa bình luận",
      commentDelete: "Xóa bình luận",
      commentDeleteConfirm: "Xóa bình luận này? Không thể hoàn tác.",
      taskDeleteConfirm: "Xóa công việc này? Không thể hoàn tác.",
      backToBacklogs: "Quay lại Backlogs",
    },
    common: {
      save: "Lưu",
      cancel: "Hủy",
      edit: "Sửa",
      delete: "Xóa",
      create: "Tạo mới",
      loading: "Đang tải...",
      noData: "Không có dữ liệu",
    },
    taskLink: {
      notFound: "Không tìm thấy công việc này, hoặc bạn không có quyền truy cập.",
    },
    taskForm: {
      createTitle: "Tạo Công Việc",
      editTitle: "Chỉnh Sửa Công Việc",
      titleLabel: "Tiêu đề",
      titlePlaceholder: "Nhập tiêu đề công việc",
      descriptionLabel: "Mô tả",
      descriptionPlaceholder: "Nhập mô tả công việc...",
      typeLabel: "Loại",
      statusLabel: "Trạng thái",
      priorityLabel: "Ưu tiên",
      storyPointLabel: "Độ phức tạp",
      storyPointPlaceholder: "vd: 1, 2, 3, 5, 8",
      createdLabel: "Ngày tạo",
      assigneeLabel: "Người thực hiện",
      sprintLabel: "Sprint",
      noSprintOption: "Không có sprint",
      submitButton: "Tạo Công Việc",
      updateButton: "Cập Nhật Công Việc",
      cancelButton: "Hủy",
      requiredField: "Trường này là bắt buộc",
    },
    taskType: {
      STORY: "Yêu Cầu",
      EPIC: "Hạng mục lớn",
      ISSUE: "Vấn đề",
      BUG: "Lỗi",
    },
    taskStatus: {
      TODO: "Cần làm",
      IN_PROGRESS: "Đang thực hiện",
      IN_REVIEW: "Đang xem xét",
      DONE: "Hoàn thành",
      CLOSED: "Đã đóng",
    },
    taskPriority: {
      HIGHEST: "Cao nhất",
      HIGH: "Cao",
      MEDIUM: "Trung bình",
      LOW: "Thấp",
    },
    toast: {
      taskCreated: "Tạo công việc thành công",
      taskUpdated: "Cập nhật công việc thành công",
      taskDeleted: "Xóa công việc thành công",
      taskCreateFailed: "Tạo công việc thất bại",
      taskUpdateFailed: "Cập nhật công việc thất bại",
      taskDeleteFailed: "Xóa công việc thất bại",
      commentAdded: "Đã thêm bình luận",
      commentAddFailed: "Không thể thêm bình luận",
      commentsLoadFailed: "Không thể tải bình luận",
      commentUpdateFailed: "Không thể cập nhật bình luận",
      commentDeleteFailed: "Không thể xóa bình luận",
      projectCreated: "Tạo dự án thành công",
      projectCreateFailed: "Tạo dự án thất bại",
      projectUpdated: "Cập nhật dự án thành công",
      projectUpdateFailed: "Cập nhật dự án thất bại",
      memberAdded: "Thêm thành viên thành công",
      memberUpdated: "Cập nhật thành viên thành công",
      memberRemoved: "Xóa thành viên thành công",
      memberAddFailed: "Thêm thành viên thất bại",
      memberUpdateFailed: "Cập nhật thành viên thất bại",
      memberRemoveFailed: "Xóa thành viên thất bại",
      sprintCreated: "Tạo sprint thành công",
      sprintUpdated: "Cập nhật sprint thành công",
      sprintDeleted: "Xóa sprint thành công",
      sprintCompleted: "Hoàn thành sprint thành công",
      sprintCreateFailed: "Tạo sprint thất bại",
      sprintUpdateFailed: "Cập nhật sprint thất bại",
      sprintDeleteFailed: "Xóa sprint thất bại",
      sprintCompleteFailed: "Hoàn thành sprint thất bại",
      apiClientUnavailable: "Không thể kết nối dịch vụ",
      genericError: "Đã xảy ra lỗi",
      membersLoadFailed: "Không thể tải thành viên dự án",
      sprintsLoadFailed: "Không thể tải sprint",
    },
    widgets: {
      pomodoro: {
        title: "Bộ đếm Pomodoro",
        start: "Bắt đầu",
        pause: "Tạm dừng",
        reset: "Đặt lại",
      },
      greeting: {
        morning: "Chào buổi sáng",
        afternoon: "Chào buổi chiều",
        evening: "Chào buổi tối",
        description: "Lời chào thân thiện đến bạn của tôi",
      },
    },
  },
} as const;

export type TaskboltTranslations = typeof taskboltTranslations.en;
